import { Component, Input, OnChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  IBillingAccount,
  IBillingTransactions,
  IEMData,
  IInstalmentPlanInformation,
  PaymentStatus,
} from '@dch/billing-business-logic';
import { Observable } from 'rxjs';
import { ApplicationState } from 'src/app/state/reducers';
import { selectTransactionsByAccountNumber } from 'src/app/state/selectors/billing-transactions.selectors';
import { OnDestroyHookDirective } from '../../common/hooks';
import { filter, tap } from 'rxjs/operators';
import { areAllBillingDocumentsPaymentStatus } from '../../services/billing-show-pay-button.service';
import { CustomerAccount } from '@dch/common-business-logic';
import { TranslationService } from '@dch/ng-common';

@Component({
  selector: 'my-bill-billing-account-container',
  templateUrl: './my-bill-billing-account-container.component.html',
  standalone: false,
})
export class MyBillBillingAccountContainerComponent
  extends OnDestroyHookDirective
  implements OnChanges
{
  @Input() billingAccount: IBillingAccount;
  @Input() isSelectedBillingAccountView: boolean;
  @Input() customerAccount?: CustomerAccount;

  transactions$?: Observable<IEMData<IBillingTransactions>>;
  showMoreButtonClicked = false;
  showNothingToPayCard = false;
  hasInstalmentPlan = false;
  noOpenBills = false;
  hasClosedBills = false;
  balance: number;
  instalmentPlanInformation?: IInstalmentPlanInformation;
  hasUnassignedFinancialTransactions = false;
  hasOnlyInstalmentPaymentStatusDocuments = false;
  isResUser: boolean;
  openBillsText = '';
  noOpenBillsText = '';
  paidBillsText = '';
  showPaidBillsText = '';

  constructor(
    private store: Store<ApplicationState>,
    private translationService: TranslationService
  ) {
    super();
  }

  ngOnChanges() {
    this.balance = Number(this.billingAccount.balance);

    this.transactions$ = this.store.select(
      selectTransactionsByAccountNumber(this.billingAccount.accountNumber)
    );

    this.setResUserTranslations();

    this.addSubscription(
      this.transactions$
        .pipe(
          filter(
            (transactions) => transactions && transactions.result !== null
          ),
          tap((transactions) => {
            const hasBillingDocuments =
              transactions.result.openTransactions.billingDocuments.length > 0;
            this.hasUnassignedFinancialTransactions =
              transactions.result.openTransactions
                .unassignedFinancialTransactions.length > 0;
            this.noOpenBills =
              !hasBillingDocuments && !this.hasUnassignedFinancialTransactions;
            if (hasBillingDocuments) {
              this.hasOnlyInstalmentPaymentStatusDocuments =
                areAllBillingDocumentsPaymentStatus(
                  transactions.result.openTransactions.billingDocuments,
                  PaymentStatus.INSTALMENT
                );
            }
            this.hasClosedBills =
              transactions.result.recentClosedTransactions.length > 0;
            this.hasInstalmentPlan =
              transactions.result.hasOwnProperty('instalmentPlanInformation') &&
              Object.keys(transactions.result.instalmentPlanInformation)
                .length !== 0;
            if (this.hasInstalmentPlan) {
              this.instalmentPlanInformation =
                transactions.result.instalmentPlanInformation;
              if (this.instalmentPlanInformation?.amount && this.balance > 0) {
                this.balance =
                  this.balance - Number(this.instalmentPlanInformation?.amount);
              }
            }
            this.showNothingToPayCard =
              this.balance === 0 ||
              (this.hasOnlyInstalmentPaymentStatusDocuments &&
                !this.hasUnassignedFinancialTransactions);
          })
        )
        .subscribe()
    );
  }

  onShowMoreBillsButtonClicked() {
    this.showMoreButtonClicked = !this.showMoreButtonClicked;
  }

  setResUserTranslations() {
    this.isResUser =
      this.customerAccount?.getSelectedCustomer().subsegment === 'RES';
    const resUserKey = this.isResUser ? 'RES.' : '';
    this.noOpenBillsText = this.translationService.getTranslatedValueFor(
      `bills.redesign.transactions.${resUserKey}noOpenBills`
    );

    this.openBillsText = this.translationService.getTranslatedValueFor(
      `bills.redesign.transactions.${resUserKey}openBills`
    );

    this.showPaidBillsText = this.translationService.getTranslatedValueFor(
      `bills.redesign.transactions.${resUserKey}showPaidBills`
    );

    this.paidBillsText = this.translationService.getTranslatedValueFor(
      `bills.redesign.transactions.${resUserKey}paidBills`
    );
  }
}
